import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Select,
} from "@headlessui/react";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import ReactStars from "react-stars";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { getApiCaller } from "../../libs/apiCaller";

const ManualReview = ({
  isOpen,
  onClose,
  reviewId,
  providerId,
  handleProviderReview,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showConsumerList, setShowConsumerList] = useState([]);

  // Yup validation schema
  const validationSchema = Yup.object({
    consumer_id: Yup.string().required("Consumer is required"),
    rating: Yup.number().min(0.5, "Rating is required").required(),
    review: Yup.string().required("Feedback is required"),
  });

  const formik = useFormik({
    initialValues: {
      consumer_id: "",
      rating: 0,
      review: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const endPoint = `add_review?review=${values.review}&rating=${
          values.rating
        }&booking_id=${0}&provider_id=${providerId}&consumer_id=${
          values.consumer_id
        }`;

        const response = await getApiCaller(endPoint);
        if (response?.data.response_code === 200) {
          toast.success(response.data.response_message);
          handleProviderReview && handleProviderReview();
          onClose();
          resetForm();
        } else {
          toast.error(response?.data?.response_message);
        }
        setSubmitting(false);
      } catch (error) {
        toast.error(error?.message);
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    getConsumerList();
  }, []);

  const getConsumerList = async () => {
    setIsLoading(true);
    try {
      const response = await getApiCaller(`consumers_list`);
      if (response?.data?.response_code === 200) {
        const consumers = response?.data?.response_data;
        setShowConsumerList(consumers);
      }
    } catch (error) {
      console.log("Error fetching consumers:", error);
    }
    setIsLoading(false);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
        formik.resetForm();
      }}
      className="relative z-50"
    >
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity" />
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative w-full transform overflow-hidden bg-white text-left shadow-xl transition-all rounded-lg sm:max-w-md md:max-w-lg">
            <div className="bg-[#21140E0F] px-4 pb-6 pt-3 sm:px-6">
              <div className="flex justify-between items-center mb-4">
                <DialogTitle className="text-lg sm:text-xl font-bold text-[#284184]">
                  {reviewId ? "Edit" : "Add"} Manual Review
                </DialogTitle>
                <button
                  onClick={() => {
                    onClose();
                    formik.resetForm();
                  }}
                  className="text-[#1C2024] p-1.5 rounded-full hover:bg-gray-100"
                >
                  <IoMdClose className="text-xl sm:text-2xl" />
                </button>
              </div>

              <form onSubmit={formik.handleSubmit} className="space-y-4">
                {/* Consumer List */}
                <p className="mb-2 font-semibold">
                  Select Consumer <span className="text-red-500">*</span>
                </p>
                <div className="relative">
                  <Select
                    name="consumer_id"
                    aria-label="Select Consumer"
                    className={`block w-full appearance-none rounded-lg bg-white/5 data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25 *:text-black border  px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 !m-0 ${
                      formik.touched.consumer_id && formik.errors.consumer_id
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    value={formik.values.consumer_id}
                    onChange={formik.handleChange}
                    disabled={isLoading}
                  >
                    <option value="" disabled>
                      Select Consumer
                    </option>
                    {showConsumerList.map((res, index) => (
                      <option
                        key={index}
                        value={res?.id}
                      >{`${res?.first_name} ${res?.last_name}`}</option>
                    ))}
                  </Select>
                  <MdOutlineKeyboardArrowDown
                    className="group pointer-events-none absolute top-2.5 right-2.5 size-4 "
                    aria-hidden="true"
                  />
                </div>
                {formik.touched.consumer_id && formik.errors.consumer_id && (
                  <p className="text-red-500 text-sm !mt-1">
                    {formik.errors.consumer_id}
                  </p>
                )}

                {/* Rating Stars */}
                <div className="mb-4">
                  <p className="mb-2 font-semibold">
                    Ratings <span className="text-red-500">*</span>
                  </p>
                  <ReactStars
                    count={5}
                    value={formik.values.rating}
                    onChange={(newRating) =>
                      formik.setFieldValue("rating", newRating)
                    }
                    size={50}
                    color2={"#ffd700"}
                    className="cursor-pointer transition-transform duration-300 -mt-5"
                  />
                  {formik.touched.rating && formik.errors.rating && (
                    <p className="text-red-500 text-sm">
                      {formik.errors.rating}
                    </p>
                  )}
                </div>

                {/* Feedback Field */}
                <div>
                  <label className="block font-semibold mb-1">
                    Feedback <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="review"
                    className={`w-full rounded-xl border  px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      formik.touched.review && formik.errors.review
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    rows="4"
                    placeholder="Write Something..."
                    value={formik.values.review}
                    onChange={formik.handleChange}
                  />
                  {formik.touched.review && formik.errors.review && (
                    <p className="text-red-500 text-sm">
                      {formik.errors.review}
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      formik.resetForm();
                    }}
                    className="w-full rounded-xl border font-semibold border-[#F04438] bg-white px-4 py-2 text-sm text-[#F04438] hover:bg-red-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="w-full rounded-xl border font-semibold border-transparent bg-[#284184] px-4 py-2 text-sm text-white hover:bg-[#1f3266] transition-colors"
                    disabled={formik.isSubmitting}
                  >
                    {formik.isSubmitting ? "Loading..." : "Add Review"}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default ManualReview;
